import { useCallback, useEffect, useState } from 'react';
import API from '../api';
import { SysMailAddressForm, MailAddressGuide } from './SysMailAddress';

// ── 환경설정 > 회사정보 : 메일 시스템 선택 카드 ───────────────────────────
// 시스템메일 : IMS가 직접 메일서버를 운영한다(등록한 도메인으로 직원별 주소를 발급, SMTP 25번 포트 수신).
// 외부메일   : 지금처럼 전자메일 메뉴에서 각자 IMAP/POP3 계정을 연결해 사용한다(아무것도 바뀌지 않음).
// 이 카드는 company_info 테이블과 무관하게 sys_mail_config 에 저장되므로, 회사정보 저장 버튼과 별개로 동작한다.

const card = { border: '1px solid #dbe4f0', background: '#f8fbff', borderRadius: 10, padding: '14px 16px', marginBottom: 16 };
const inp = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none', background: '#fff' };
const sbtn = (bg) => ({ padding: '8px 14px', background: bg, color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 });
const obtn = { padding: '8px 14px', background: '#fff', color: '#185FA5', border: '1px solid #185FA5', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 };

function Overlay({ title, width = 720, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 22, width, maxWidth: '94vw', maxHeight: '88vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function DnsGuideModal({ onClose, showMsg }) {
  const [g, setG] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { API.get('/sysmail/dns-guide').then(r => setG(r.data)).catch(e => showMsg && showMsg(e?.response?.data?.message || 'DNS 안내를 불러오지 못했습니다.', 'error')); }, [showMsg]);
  useEffect(() => { load(); }, [load]);
  const genDkim = async () => {
    setBusy(true);
    try { await API.post('/sysmail/dkim/generate', {}); load(); showMsg && showMsg('DKIM 키를 만들었습니다. 아래 TXT 값을 DNS에 등록하세요.', 'success'); }
    catch (e) { showMsg && showMsg(e?.response?.data?.message || 'DKIM 키 생성 실패', 'error'); }
    setBusy(false);
  };
  const copy = (t) => { try { navigator.clipboard.writeText(t); showMsg && showMsg('복사했습니다.', 'success'); } catch (e) { /* ignore */ } };
  return (
    <Overlay title="DNS 설정 안내" width={860} onClose={onClose}>
      {!g ? <div style={{ padding: 20, color: '#888' }}>불러오는 중...</div> : (
        <>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 10 }}>
            도메인 <b>{g.domain}</b> 을(를) 구입한 곳(가비아 등)의 DNS 관리 화면에서 아래 레코드를 등록해야 외부에서 이 서버로 메일이 들어오고, 보낸 메일이 스팸 처리되는 것을 줄일 수 있습니다.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: '#f3f4f6' }}>{['종류', '호스트', '값', '설명'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>{h}</th>)}</tr></thead>
            <tbody>
              {g.records.map((r, i) => (
                <tr key={i}>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontWeight: 700 }}>{r.type}</td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', wordBreak: 'break-all' }}>{r.host}</td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 12 }}>
                    {r.value} {!/^\(/.test(r.value) && <button onClick={() => copy(r.value)} style={{ marginLeft: 6, fontSize: 11, border: '1px solid #ccc', background: '#fff', borderRadius: 4, cursor: 'pointer' }}>복사</button>}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: '#6b7280' }}>{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={genDkim} disabled={busy || g.dkim.exists} style={{ ...sbtn(g.dkim.exists ? '#9ca3af' : '#185FA5') }}>{g.dkim.exists ? 'DKIM 키 생성됨' : 'DKIM 키 생성'}</button>
            <span style={{ fontSize: 12, color: '#6b7280' }}>DKIM은 보낸 메일에 전자서명을 붙여 신뢰도를 높입니다(선택).</span>
          </div>
          <div style={{ marginTop: 16, padding: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12.5, color: '#92400e', lineHeight: 1.7 }}>
            <b>꼭 확인하세요</b>
            <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>{g.checklist.map((c, i) => <li key={i}>{c}</li>)}</ul>
          </div>
        </>
      )}
    </Overlay>
  );
}

function MailboxAdminModal({ domain, onClose, showMsg }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => { API.get('/sysmail/admin/mailboxes').then(r => setRows(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <Overlay title="직원 메일주소 관리" width={820} onClose={onClose}>
      <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 8 }}>직원이 전자메일 메뉴에서 직접 등록할 수도 있고, 여기서 관리자가 대신 지정/변경할 수 있습니다. 등록된 주소는 전자메일 작성창 자동완성·주소록 등 IMS 전체에서 공유됩니다.</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ background: '#f3f4f6' }}>{['부서', '직급', '이름', '아이디', '메일주소', ''].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.username}>
              <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9' }}>{r.department || '-'}</td>
              <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9' }}>{r.position || '-'}</td>
              <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9' }}>{r.name}</td>
              <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9', color: '#6b7280' }}>{r.username}</td>
              <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9' }}>{r.address || <span style={{ color: '#b45309' }}>미등록</span>}</td>
              <td style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>
                <button onClick={() => setEdit(r)} style={{ ...obtn, padding: '4px 10px', fontSize: 12 }}>{r.address ? '변경' : '지정'}</button>
              </td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>직원이 없습니다.</td></tr>}
        </tbody>
      </table>
      {edit && (
        <Overlay title={`${edit.name} 메일주소`} width={480} onClose={() => setEdit(null)}>
          <SysMailAddressForm domain={domain} initial={edit.local_part || ''} username={edit.username} admin
            onSaved={() => { setEdit(null); load(); showMsg && showMsg('메일주소를 저장했습니다.', 'success'); }} onCancel={() => setEdit(null)} />
        </Overlay>
      )}
    </Overlay>
  );
}

export default function SysMailConfigCard({ showMsg }) {
  const [cfg, setCfg] = useState(null);
  const [denied, setDenied] = useState(false);
  const [mode, setMode] = useState('external');
  const [domain, setDomain] = useState('');
  const [publicUrl, setPublicUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDns, setShowDns] = useState(false);
  const [showBoxes, setShowBoxes] = useState(false);
  const [sender, setSender] = useState('');
  const [senderName, setSenderName] = useState('');
  const [rl, setRl] = useState({ smtp_port: '', pop3_port: '' });
  const [refMin, setRefMin] = useState('5');
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);

  const load = useCallback(() => {
    API.get('/sysmail/config').then(r => {
      setCfg(r.data); setMode(r.data.mode); setDomain(r.data.domain || ''); setPublicUrl(r.data.public_url || '');
      setSender(r.data.system_sender || 'noreply'); setSenderName(r.data.system_sender_name || '');
      setRefMin(String(r.data.refresh_minutes || 5));
      const c = r.data.relay_cfg || {};
      setRl({ smtp_port: c.smtp_port ? String(c.smtp_port) : '', pop3_port: c.pop3_port ? String(c.pop3_port) : '' });
    }).catch(e => { if (e?.response?.status === 403) setDenied(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (denied || !cfg) return null; // 관리자가 아니면 표시하지 않는다(서버도 관리자만 허용)

  const rc = cfg.relay_cfg || {};
  const relayDirty = String(rl.smtp_port || '') !== String(rc.smtp_port || '') || String(rl.pop3_port || '') !== String(rc.pop3_port || '');
  const relayBody = () => ({ smtp_port: rl.smtp_port === '' ? '' : Number(rl.smtp_port), pop3_port: rl.pop3_port === '' ? '' : Number(rl.pop3_port) });
  const testRelay = async () => {
    setTesting(true); setTestRes(null);
    try { const r = await API.post('/sysmail/relay/test', { relay: relayBody(), to: testTo.trim() || undefined }); setTestRes(r.data); }
    catch (e) { setTestRes({ ok: false, error: e?.response?.data?.message || '테스트에 실패했습니다.' }); }
    setTesting(false);
  };
  const dirty = mode !== cfg.mode || domain.trim().toLowerCase() !== (cfg.domain || '') || publicUrl.trim() !== (cfg.public_url || '')
    || (mode === 'system' && (sender.trim().toLowerCase() !== (cfg.system_sender || 'noreply') || senderName.trim() !== (cfg.system_sender_name || '')))
    || relayDirty || String(refMin) !== String(cfg.refresh_minutes || 5);
  const save = async () => {
    if (mode === 'system' && !domain.trim()) { showMsg && showMsg('시스템메일을 사용하려면 도메인을 입력하세요.', 'error'); return; }
    if (mode === 'external' && cfg.mode === 'system' && !window.confirm('외부메일로 전환하면 자체 메일 수신이 중지됩니다(저장된 메일은 보관됩니다). 계속할까요?')) return;
    const rm = Number(refMin);
    if (!Number.isInteger(rm) || rm < 1 || rm > 120) { showMsg && showMsg('새로고침 간격은 1~120분 사이 정수로 입력하세요.', 'error'); return; }
    setSaving(true);
    try {
      const body = { refresh_minutes: rm, mode, domain: domain.trim(), public_url: publicUrl.trim(), relay: relayBody() };
      if (mode === 'system') { body.system_sender = sender.trim().toLowerCase(); body.system_sender_name = senderName.trim(); }
      const r = await API.put('/sysmail/config', body);
      setCfg(c => ({ ...c, ...r.data })); load();
      showMsg && showMsg(mode === 'system' ? '시스템메일 설정을 저장했습니다. DNS 안내를 확인하세요.' : '외부메일(기존 방식)로 설정했습니다.', 'success');
    } catch (e) { showMsg && showMsg((e?.response?.data?.message || (e?.response ? '저장하지 못했습니다. (서버 응답 ' + e.response.status + ')' : '서버에 연결하지 못했습니다.')), 'error'); }
    setSaving(false);
  };

  const smtp = cfg.smtp || {};
  const radio = (v, title, desc) => (
    <label style={{ flex: 1, display: 'flex', gap: 8, padding: '10px 12px', border: `1px solid ${mode === v ? '#185FA5' : '#d1d5db'}`, background: mode === v ? '#eaf3fc' : '#fff', borderRadius: 8, cursor: 'pointer' }}>
      <input type="radio" name="sysmail_mode" checked={mode === v} onChange={() => setMode(v)} style={{ marginTop: 3 }} />
      <span><b style={{ fontSize: 14 }}>{title}</b><br /><span style={{ fontSize: 12, color: '#6b7280' }}>{desc}</span></span>
    </label>
  );

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#185FA5' }}>메일 시스템</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {cfg.mode === 'system' && <button type="button" onClick={() => setShowBoxes(true)} style={obtn}>직원 메일주소 관리</button>}
          {cfg.domain && <button type="button" onClick={() => setShowDns(true)} style={obtn}>DNS 설정 안내</button>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        {radio('external', '외부메일', '현재 방식 유지 — 전자메일 메뉴에서 각자 IMAP/POP3 계정을 연결')}
        {radio('system', '시스템메일', 'IMS 자체 메일서버 — 등록한 도메인으로 직원별 메일주소 발급')}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={{ fontSize: 13, color: '#555', fontWeight: 500, display: 'block', marginBottom: 4 }}>메일 도메인</label>
          <input style={inp} value={domain} onChange={e => setDomain(e.target.value.replace(/\s/g, '').toLowerCase())} />
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>직원 메일주소는 <b>아이디@{domain || '도메인'}</b> 형태가 됩니다.</div>
        </div>
        <div>
          <label style={{ fontSize: 13, color: '#555', fontWeight: 500, display: 'block', marginBottom: 4 }}>IMS 접속 주소 (외부 수신확인용, 선택)</label>
          <input style={inp} value={publicUrl} onChange={e => setPublicUrl(e.target.value.trim())} />
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>외부로 보낸 메일의 열람 여부를 확인할 때 쓰는 인터넷 주소입니다.</div>
        </div>
      </div>
      {mode === 'system' && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>시스템 발신 주소</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 12 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input style={inp} value={sender} onChange={e => setSender(e.target.value.replace(/\s/g, '').toLowerCase())} />
                <span style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>@{domain || '도메인'}</span>
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>로그인 인증번호·전송실패 안내 등 <b>시스템이 보내는 모든 메일</b>의 보낸 사람 주소입니다. 저장하면 이 주소의 메일함이 자동 생성됩니다.</div>
            </div>
            <div>
              <input style={inp} value={senderName} placeholder="보낸 사람 이름 (예: IMS 알림)" onChange={e => setSenderName(e.target.value)} />
              {cfg.system_sender_address && <div style={{ fontSize: 12, color: '#15803d', marginTop: 4 }}>● 현재 발신: {cfg.system_sender_address}</div>}
            </div>
          </div>
        </div>
      )}
      <div style={{ marginTop: 12, padding: '10px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>외부 발송(릴레이) — 해외 포함 외부로 보내기</div>
        <div style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 8px', lineHeight: 1.6 }}>
          서버 주소·사용자 계정·비밀번호는 <b>전자메일 메뉴</b>에 등록한 계정의 값을 사용합니다. 여기서는 포트만 지정하세요(비워두면 계정에 등록된 포트 사용).
          {rc.source === 'account' && ` 현재 연동 계정: ${rc.account_label || rc.user} (${rc.host}:${rc.port})`}
          {rc.source === 'direct' && ' 전자메일 메뉴에 등록된 계정이 없어 직접 발송 중입니다(국내 회선은 25번 포트가 막혀 있을 수 있음).'}
          {rc.source === 'env' && ' (서버 .env 의 릴레이 설정을 사용 중입니다.)'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={{ fontSize: 12.5, color: '#555', display: 'block', marginBottom: 3 }}>SMTP 포트 (발송 · 465=SSL, 587=STARTTLS)</label>
            <input style={inp} type="number" value={rl.smtp_port} onChange={e => setRl({ ...rl, smtp_port: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 12.5, color: '#555', display: 'block', marginBottom: 3 }}>POP3 포트 (수신 · 995=SSL, 110)</label>
            <input style={inp} type="number" value={rl.pop3_port} onChange={e => setRl({ ...rl, pop3_port: e.target.value })} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input style={{ ...inp, width: 260 }} placeholder="시험 메일 받을 주소 (선택)" value={testTo} onChange={e => setTestTo(e.target.value.trim())} />
          <button type="button" onClick={testRelay} disabled={testing} style={obtn}>{testing ? '확인 중...' : '연결 테스트'}</button>
          {testRes && (
            <span style={{ fontSize: 12.5, color: testRes.ok ? '#15803d' : '#b91c1c' }}>
              {testRes.ok ? `✔ 연결 성공 (${testRes.ms}ms)${testRes.sent ? ` · 시험 메일 발송: ${testRes.sent}` : ''}` : `✖ ${testRes.error}`}
            </span>
          )}
        </div>
        {relayDirty && <div style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>※ 변경 내용은 아래 [메일 설정 저장]을 눌러야 적용됩니다. 시험 메일은 저장된 시스템 발신 주소로 발송됩니다.</div>}
      </div>
      <div style={{ marginTop: 12, padding: '10px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>새로고침 시간 간격</div>
        <input style={{ ...inp, width: 80 }} type="number" min={1} max={120} value={refMin} onChange={e => setRefMin(e.target.value)} />
        <span style={{ fontSize: 13, color: '#555' }}>분</span>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>새 메일 확인 주기입니다. 기본 5분(1~120분). 저장 후 다음 로그인/화면 열기부터 적용됩니다.</span>
      </div>
      <MailAddressGuide style={{ marginTop: 10 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
        <button type="button" onClick={save} disabled={saving || !dirty} style={sbtn(saving || !dirty ? '#9ca3af' : '#185FA5')}>{saving ? '저장 중...' : '메일 설정 저장'}</button>
        {cfg.mode === 'system' && (
          <span style={{ fontSize: 12.5, color: smtp.listening ? '#15803d' : '#b45309' }}>
            {smtp.listening ? `● 메일 수신 서버 동작 중 (포트 ${smtp.port})` : `● 메일 수신 서버 중지됨${smtp.error ? ` — ${smtp.error}` : ''}`}
          </span>
        )}
      </div>
      {cfg.mode === 'system' && !smtp.listening && (
        <div style={{ fontSize: 12, color: '#92400e', marginTop: 6, lineHeight: 1.6 }}>
          25번 포트는 다른 프로그램이 쓰고 있거나 관리자 권한이 필요할 수 있습니다. 이 경우 서버 .env 의 SYSMAIL_SMTP_PORT 로 다른 포트를 지정하세요. (서버 재시작 필요)
          {cfg.relay ? ' 외부 발송은 릴레이 설정을 사용 중입니다.' : ''}
        </div>
      )}
      {showDns && <DnsGuideModal onClose={() => setShowDns(false)} showMsg={showMsg} />}
      {showBoxes && <MailboxAdminModal domain={cfg.domain} onClose={() => setShowBoxes(false)} showMsg={showMsg} />}
    </div>
  );
}
